import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Circus Daily StepProgress (numeric) — Figma 10023:34642
 * Used on gameplay pick progress. Each step can complete independently.
 */
export type StepProgressProps = {
  /** Per-step completion (true = check, false = number) */
  steps: boolean[];
  className?: string;
  /** Per-step override (e.g. tiebreaker star). Return null/undefined for default. */
  renderStep?: (index: number, isComplete: boolean) => React.ReactNode;
};

const StepProgress = React.forwardRef<HTMLDivElement, StepProgressProps>(
  ({ steps, className, renderStep }, ref) => {
    const { t } = useTranslation();
    const total = steps.length;
    const completed = steps.filter(Boolean).length;

    return (
      <div
        ref={ref}
        className={cn('flex w-full items-center', className)}
        data-test-id="step-progress"
        role="list"
        aria-label={t('a11y.progress', { completed, total })}
      >
        {steps.map((isComplete, index) => {
          const isLast = index === total - 1;
          // Connector after a completed step is active (Figma 10203:51121)
          const connectorActive = isComplete;
          const custom = renderStep?.(index, isComplete);
          const useCustom = Boolean(renderStep) && custom != null;

          return (
            <React.Fragment key={index}>
              <div
                role="listitem"
                data-test-id={`progress-circle-${index + 1}`}
                data-test-answered={isComplete}
                className={cn(
                  'relative flex size-7 shrink-0 items-center justify-center rounded-full p-0.5',
                  isComplete ? 'bg-white' : 'bg-surface-muted'
                )}
              >
                {useCustom ? (
                  custom
                ) : isComplete ? (
                  <Check className="size-5 stroke-[3] text-primary" aria-hidden />
                ) : (
                  <span className="font-sans text-cta font-semibold capitalize text-white">
                    {index + 1}
                  </span>
                )}
              </div>
              {!isLast && (
                <div
                  className={cn(
                    'mx-[-1px] h-1 min-w-px flex-1',
                    connectorActive ? 'bg-white' : 'bg-surface-muted'
                  )}
                  aria-hidden
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }
);
StepProgress.displayName = 'StepProgress';

export { StepProgress };
